import {
  Stepper,
  Text,
  Radio,
  Stack,
  Button,
  Group,
  Skeleton,
  Box,
} from "@mantine/core";
import { TrimmedTextInput as TextInput } from "@benchmark/ui/components/atomic";
import { TaskForm } from "./Task.form";
import { useState, useEffect, useMemo, startTransition } from "react";
import { useMedplum } from "@medplum/react";
import type { Task } from "@medplum/fhirtypes";
import { showSuccessNotification } from "@benchmark/ui/utils";

/**
 * Props for the ArchiveForm component.
 * @agentic connections
 */
type ArchiveFormProps = {
  taskId?: string | null;
  opened?: boolean; // parent modal passes true when visible
  onArchive?: (task: Task, reason: string) => void;
  onCancel?: () => void;
  initialStep?: number;
  readOnlyReview?: boolean;
};

/**
 * Stepper form for archiving a task with reason, optional follow-up, and review.
 * @agentic connections
 */
export const ArchiveForm = ({
  taskId,
  opened = true,
  onArchive,
  onCancel,
  initialStep = 0,
  readOnlyReview = false,
}: ArchiveFormProps): React.JSX.Element => {
  const [active, setActive] = useState(initialStep);
  const [reason, setReason] = useState<string>("");
  const [otherReason, setOtherReason] = useState<string>("");
  const [followUpTasks, setFollowUpTasks] = useState<Record<string, Task>>({});
  const currentFollowUpTask = useMemo(() => {
    if (!taskId) return null;
    return followUpTasks[taskId] ?? null;
  }, [followUpTasks, taskId]);
  const [task, setTask] = useState<Task | null>(null);
  const [loadingTask, setLoadingTask] = useState<boolean>(false);
  const medplum = useMedplum();

  const reasonOptions = [
    "Member unable to reach",
    "No longer applicable to member",
    "Action item created by mistake",
    "Other",
  ];

  const canProceed = () => {
    if (active === 0) {
      if (!reason) return false;
      if (reason === "Other" && otherReason.trim().length === 0) return false;
    }
    return true;
  };

  const handleNext = () => {
    if (!canProceed()) return;
    if (active === 2) {
      // Final step, call archive
      if (task && onArchive) {
        const finalReason = reason === "Other" ? otherReason.trim() : reason;
        onArchive(task, finalReason);
      }
      onCancel?.();
      return;
    }
    setActive((prev) => prev + 1);
  };

  const handleBack = () => setActive((prev) => Math.max(prev - 1, 0));

  // Load task whenever taskId changes or modal opens
  useEffect(() => {
    if (!taskId || !opened) {
      startTransition(() => {
        setTask(null);
      });
      return;
    }
    let cancelled = false;
    startTransition(() => {
      setLoadingTask(true);
    });
    medplum
      .readResource("Task", taskId)
      .then((res) => {
        if (!cancelled) {
          startTransition(() => {
            setTask(res as Task);
          });
        }
      })
      .finally(() => {
        if (!cancelled) {
          startTransition(() => {
            setLoadingTask(false);
          });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [taskId, opened, medplum]);

  // Reset wizard state when taskId changes/open changes
  useEffect(() => {
    startTransition(() => {
      setActive(initialStep);
      setReason("");
      setOtherReason("");
    });
  }, [taskId, initialStep, opened]);

  if (loadingTask) {
    return (
      <Box p="lg">
        <Skeleton height={20} mb="sm" />
        <Skeleton height={12} width="70%" />
      </Box>
    );
  }

  return (
    <Stack gap="xl">
      <Group justify="center" pt="xs">
        <Stepper active={active} onStepClick={setActive} size="md">
          <Stepper.Step label="Reason" description="Reason" />
          <Stepper.Step label="Follow-up" description="Follow-up" />
          <Stepper.Step label="Review" description="Review" />
        </Stepper>
      </Group>

      <Stack mt="xl" gap="xl" px="md" pb="xl">
        {active === 0 && (
          <Stack gap="md">
            <Text fw={600} size="lg">
              Archive Reason
            </Text>
            <Text c="dimmed" size="sm">
              Why are you archiving this task?
            </Text>

            <Radio.Group
              value={reason}
              onChange={(val) => {
                setReason(val);
                if (val !== "Other") {
                  setOtherReason("");
                }
              }}
            >
              <Stack>
                {reasonOptions.map((opt) => (
                  <Radio key={opt} value={opt} label={opt} size="md" />
                ))}
              </Stack>
            </Radio.Group>

            {reason === "Other" && (
              <TextInput
                label="Other Reason"
                placeholder="Provide additional details (200 character max)"
                value={otherReason}
                onChange={(e) => {
                  if (e.target.value.length <= 200) {
                    setOtherReason(e.target.value);
                  }
                }}
                withAsterisk
                description={`${otherReason.length}/200 characters`}
              />
            )}
          </Stack>
        )}
        {active === 1 && (
          <Stack gap="md">
            <Text fw={600} size="lg">
              Add Follow-up (Optional)
            </Text>
            <Text c="dimmed" size="sm">
              You can add a follow-up before archiving this task.
            </Text>

            {currentFollowUpTask ? (
              <Text c="green" fw={500}>
                Follow-up task created successfully.
              </Text>
            ) : (
              <TaskForm
                patientId={task?.for?.reference?.split("/")[1]}
                onSuccess={async (newTaskId: string) => {
                  try {
                    const created = (await medplum.readResource(
                      "Task",
                      newTaskId,
                    )) as Task;
                    setFollowUpTasks((prev) => ({
                      ...prev,
                      [task?.id as string]: created,
                    }));
                    showSuccessNotification("Follow-up task created");
                  } catch {
                    /* ignore */
                  }
                }}
              />
            )}
          </Stack>
        )}

        {active === 2 && (
          <Stack gap="md">
            <Text fw={600} size="lg">
              Review & Confirm
            </Text>

            <Text>
              <strong>Reason for Archive:</strong>{" "}
              {reason === "Other" ? otherReason : reason}
            </Text>

            {currentFollowUpTask && (
              <Stack gap="xs">
                <Text fw={500}>Follow-up Task Summary</Text>
                <Text>
                  Description: {currentFollowUpTask.description || "(none)"}
                </Text>
                {currentFollowUpTask.restriction?.period?.end &&
                  (() => {
                    const date = new Date(
                      currentFollowUpTask.restriction.period.end,
                    );
                    if (!isNaN(date.getTime())) {
                      return <Text>Due Date: {date.toLocaleDateString()}</Text>;
                    }
                    return null;
                  })()}
                <Text>Status: {currentFollowUpTask.status}</Text>
              </Stack>
            )}
          </Stack>
        )}

        {!readOnlyReview && (
          <Group justify="space-between" mt="xl" px="md">
            <Button variant="subtle" onClick={onCancel} color="gray">
              Cancel
            </Button>
            <Group>
              {active > 0 && (
                <Button variant="default" onClick={handleBack}>
                  Back
                </Button>
              )}
              <Button onClick={handleNext} disabled={!canProceed()}>
                {active === 2
                  ? "Archive"
                  : active === 1
                    ? currentFollowUpTask
                      ? "Next"
                      : "Skip"
                    : "Next"}
              </Button>
            </Group>
          </Group>
        )}
      </Stack>
    </Stack>
  );
};
